import React from 'react';

import { dataCellClass, dataFrameClass, dataRowClass } from '@/components/admin/SysadminSPA/shared/SPADataTable';
import cn from '@/utils/cn';

import { toneText, toneTint } from './tone';
import { Change, DiffSegment, EditHref } from './types';

// Only the words that differ carry colour, as marks on the row's surface: print (the source of
// truth) in the success tone, online text that must change to match it in the error tone (no
// strikethrough — the labels disambiguate and struck text is hard to read). The rest of each side
// is plain, so a row reads as text with the edit picked out rather than two coloured washes.
const PRINT = 'tw-text-spa-primary';
const ONLINE = 'tw-text-spa-secondary';
const NOTE = 'tw-text-spa-secondary';

const mark = (tone: 'error' | 'success' | 'warning') => cn(
  'tw-rounded tw-px-0.5 tw-font-semibold', toneTint[tone], toneText[tone],
);

export const PRINT_WORDS = mark('success');
export const ONLINE_WORDS = mark('error');
// Online-only text (not in print at all) is the warning tone: it may be intentional online content.
const ONLINE_EXTRA = mark('warning');

const SegmentedText = ({ highlight, segments }: { highlight: string; segments: DiffSegment[] }) => (
  <>
    {segments.map((segment, i) => (
      <React.Fragment key={i}>
        {i > 0 && ' '}
        {segment.changed ? <span className={highlight}>{segment.text}</span> : segment.text}
      </React.Fragment>
    ))}
  </>
);

const CaptionTag = ({ change }: { change: Change }) => {
  const label = (change.caption && 'Caption:') || (change.vocab && 'Vocab:') || (change.title && 'Title:') || null;
  if (!label) return null;

  return (
    <span className="tw-mr-1 tw-text-sm tw-font-semibold tw-text-spa-accent-text">{label}</span>
  );
};

// Captions and vocabulary aren't edited in the pour editors — captions live in the image caption
// popover on the section edit page, and vocabulary renders from the glossary terms defined on a
// section's glossary panel — so point the fixer at the right place.
const EditElsewhereNote = ({ href, linkText, prefix, suffix }: {
  href: string;
  linkText: string;
  prefix: string;
  suffix: string;
}) => (
  <span className="tw-mt-1 tw-block tw-text-xs tw-text-spa-secondary">
    {prefix}{' '}
    <a
      className="tw-text-spa-accent-text hover:tw-text-spa-accent-text-hover hover:tw-underline"
      href={href}
      rel="noopener noreferrer"
      target="_blank"
    >
      {linkText}
    </a>
    {' '}{suffix}
  </span>
);

// Text laid inside a graphic rather than beside it (a diagram's flow boxes, a player's button
// labels). Online rebuilds the graphic with that text baked in, so it reads as missing content
// when it isn't. Said plainly on the row rather than filtered out — a real caption is sometimes
// laid over a large photo, so this is a hint for the reviewer, not a verdict.
const ArtworkNote = () => (
  <span className="tw-mt-1 tw-block tw-text-xs tw-text-spa-secondary">
    Sits inside the artwork — online likely shows this text as part of the image.
  </span>
);

const changeNote = (change: Change, captionEditHref?: string | null, vocabEditHref?: EditHref) => {
  if (change.relocated) return undefined;
  if (change.caption && change.inArtwork) return <ArtworkNote />;
  if (change.caption && captionEditHref) {
    return (
      <EditElsewhereNote
        href={captionEditHref}
        linkText="edit the section"
        prefix="Caption updates aren’t supported here —"
        suffix="to change it."
      />
    );
  }
  const glossaryHref = change.vocab && vocabEditHref && vocabEditHref(change.vocabSectionId ?? null);
  if (glossaryHref) {
    return (
      <EditElsewhereNote
        href={glossaryHref}
        linkText="edit its section’s glossary"
        prefix="Vocabulary renders from the glossary, not this text —"
        suffix="to change it."
      />
    );
  }
  return undefined;
};

// The unchanged words around a change, muted, so a reader can find where in the section it sits
// ("…of a citizen [map.] Maps can help…").
const ChangeContext = ({ after, text }: { after?: boolean; text?: string | null }) => {
  if (!text) return null;

  return (
    <span className={NOTE}>{after ? ` ${text}…` : `…${text} `}</span>
  );
};

// A labeled side of a diff, e.g. "PRINT compass rose" — the label makes it unambiguous which text
// is print vs online, rather than relying on color alone. `block` puts the side on its own line
// (used for changed pairs, so the two versions read as parallel lines instead of one run-on).
// With `highlight`, the differing words carry the mark when the pair was diffed word by word (a
// changed caption); otherwise the whole side is the change and wears the mark.
interface SideProps {
  block?: boolean;
  change: Change;
  className: string;
  highlight?: string;
  label: string;
  segments?: DiffSegment[] | null;
  text: string | null;
}

const Side = ({ block, change, className, highlight, label, segments, text }: SideProps) => (
  <span className={block ? 'tw-block' : undefined}>
    <span
      className="tw-mr-1 tw-inline-block tw-min-w-[3.25rem] tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide
        tw-text-spa-secondary"
    >
      {label}
    </span>
    <ChangeContext text={change.contextBefore} />
    {highlight && segments?.length ? (
      <span className={className}><SegmentedText highlight={highlight} segments={segments} /></span>
    ) : (
      <span className={highlight ?? className}>{text}</span>
    )}
    <ChangeContext after text={change.contextAfter} />
  </span>
);

// The changes are rows of an SPA list frame, so a Print/Online pair reads as one unit the way
// every other list on the SPA does. With onLocate (the pour view), the row is a button that
// scrolls/flashes the change in the editors and gets the list's hover.
const LOCATE_BUTTON = 'tw-m-0 tw-w-full tw-cursor-pointer tw-appearance-none tw-border-0 '
  + 'tw-bg-transparent tw-p-0 tw-text-left';

interface ChangeItemProps {
  children: React.ReactNode;
  className?: string;
  index: number;
  isLast: boolean;
  // Rendered after (outside) the locate button — a note may carry its own link.
  note?: React.ReactNode;
  onLocate?: () => void;
}

const ChangeItem = ({
  children, className, index, isLast, note, onLocate,
}: ChangeItemProps) => (
  <li className={cn(onLocate ? dataRowClass(index, false) : 'tw-text-spa-primary', dataCellClass(isLast))}>
    {onLocate ? (
      <button
        aria-label="Show this change in the editors"
        className={LOCATE_BUTTON}
        type="button"
        onClick={onLocate}
      >
        <span className={cn('tw-text-sm', className)}>{children}</span>
      </button>
    ) : (
      <span className={cn('tw-text-sm', className)}>{children}</span>
    )}
    {note}
  </li>
);

interface ChangeLineProps {
  captionEditHref?: string | null;
  change: Change;
  index: number;
  isLast: boolean;
  vocabEditHref?: EditHref;
  onLocate?: (change: Change) => void;
}

const ChangeLine = ({
  captionEditHref, change, index, isLast, vocabEditHref, onLocate,
}: ChangeLineProps) => {
  const locate = onLocate && (() => onLocate(change));
  const note = changeNote(change, captionEditHref, vocabEditHref);
  const row = { index, isLast, onLocate: locate };
  if (change.relocated) {
    return (
      <ChangeItem {...row} className={NOTE}>
        <CaptionTag change={change} />Present elsewhere in print (not a change): “{change.onlineText}”
      </ChangeItem>
    );
  }
  if (change.type === 'changed') {
    return (
      <ChangeItem {...row} note={note}>
        <CaptionTag change={change} />
        <Side
          block
          change={change}
          className={PRINT}
          highlight={PRINT_WORDS}
          label="Print"
          segments={change.printSegments}
          text={change.printText}
        />
        <Side
          block
          change={change}
          className={ONLINE}
          highlight={ONLINE_WORDS}
          label="Online"
          segments={change.onlineSegments}
          text={change.onlineText}
        />
      </ChangeItem>
    );
  }
  if (change.type === 'print_only') {
    return (
      <ChangeItem {...row} note={note}>
        <CaptionTag change={change} />
        <Side change={change} className={PRINT_WORDS} label="Print" text={change.printText} />{' '}
        <span className={NOTE}>— not in the online text yet</span>
      </ChangeItem>
    );
  }
  return (
    <ChangeItem {...row} note={note}>
      <CaptionTag change={change} />
      <Side change={change} className={ONLINE_EXTRA} label="Online" text={change.onlineText} />{' '}
      <span className={NOTE}>— not in the print</span>
    </ChangeItem>
  );
};

interface SectionChangesProps {
  // When set (the pour view), caption rows point at the section edit page — captions are managed
  // via the image caption popover there, not the editors on this page.
  captionEditHref?: string | null;
  changes: Change[];
  // When set (the pour view), vocab rows point at the edit page of the section whose glossary
  // panel defines the term — the vocab box renders from glossary terms, not the stored body.
  vocabEditHref?: EditHref;
  // When set (the pour view), each change row becomes a button that locates it in the editors.
  onLocate?: (change: Change) => void;
}

const SectionChanges = ({ captionEditHref, changes, vocabEditHref, onLocate }: SectionChangesProps) => (
  <ul className={cn(dataFrameClass, 'tw-m-0 tw-list-none tw-p-0')}>
    {changes.map((change, i) => (
      <ChangeLine
        key={i}
        captionEditHref={captionEditHref}
        change={change}
        index={i}
        isLast={i === changes.length - 1}
        vocabEditHref={vocabEditHref}
        onLocate={onLocate}
      />
    ))}
  </ul>
);

export default SectionChanges;
